import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Image as ImageIcon, Save, ArrowLeft } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { PROGRAMS } from "@/data";
import MediaLibrary from "@/admin/MediaLibrary";

const MED = PROGRAMS["meditation-and-mindfulness"];
const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");
const factsToText = (a) => (a || []).map((f) => `${f.label} | ${f.value}`).join("\n");
const textToFacts = (s) => linesToArr(s).map((l) => { const [label, ...v] = l.split("|"); return { label: (label || "").trim(), value: v.join("|").trim() }; });
const formatsToText = (a) => (a || []).map((f) => `${f.name} | ${f.time} | ${f.desc}`).join("\n");
const textToFormats = (s) => linesToArr(s).map((l) => { const p = l.split("|"); return { name: (p[0] || "").trim(), time: (p[1] || "").trim(), desc: (p[2] || "").trim() }; });

const Field = ({ label, children, hint }) => (
  <div className="mb-5">
    <label className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
  </div>
);
const input = "w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-magenta";

export default function PageEditor() {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug || routeSlug === "new";
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [status, setStatus] = useState("");
  const [picker, setPicker] = useState(null); // image field key

  useEffect(() => {
    const toForm = (p) => ({
      slug: p.slug || "", title: p.title || "", titleLead: p.titleLead || "", accent: p.accent || "",
      category: p.category || "Corporate Wellbeing", subtitle: p.subtitle || "",
      images: { hero: p.images?.hero || "", space: p.images?.space || "", calm: p.images?.calm || MED.images.calm },
      heroSlides: (p.heroSlides || []).map((s) => (typeof s === "string" ? s : [s.image, s.label].filter(Boolean).join(" | "))).join("\n"),
      overview: arrToLines(p.overview), techniques: arrToLines(p.techniques), tagline: p.tagline || "",
      helps: p.helps || "", orgImpact: arrToLines(p.orgImpact), orgNote: p.orgNote || "",
      whoFor: arrToLines(p.whoFor), facts: factsToText(p.facts || MED.facts), formats: formatsToText(p.formats || MED.formats),
    });
    if (isNew) { setF(toForm({ accent: "", titleLead: "" })); return; }
    api.get(`/pages/${routeSlug}`)
      .then((r) => setF(toForm(r.data)))
      .catch(() => setF(toForm(PROGRAMS[routeSlug] || { slug: routeSlug })));
  }, [routeSlug, isNew]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setImg = (k, v) => setF((p) => ({ ...p, images: { ...p.images, [k]: v } }));

  const save = async () => {
    if (!f.slug.trim()) { setStatus("error"); return; }
    setStatus("saving");
    const body = {
      slug: f.slug.trim(), title: f.title, titleLead: f.titleLead, accent: f.accent, category: f.category,
      subtitle: f.subtitle, images: f.images, overview: linesToArr(f.overview), techniques: linesToArr(f.techniques),
      heroSlides: linesToArr(f.heroSlides).map((l) => { const [image, ...r] = l.split("|"); return { image: image.trim(), label: r.join("|").trim() }; }),
      tagline: f.tagline, helps: f.helps, orgImpact: linesToArr(f.orgImpact), orgNote: f.orgNote,
      whoFor: linesToArr(f.whoFor), facts: textToFacts(f.facts), formats: textToFormats(f.formats),
    };
    try {
      await api.post("/pages", body);
      setStatus("saved");
      if (isNew) nav(`/admin/pages/${body.slug}`);
      setTimeout(() => setStatus(""), 1800);
    } catch (e) { setStatus("error"); }
  };

  const preview = useMemo(() => (f?.images ? {} : {}), [f]);
  if (!f) return <div className="text-slate-400">Loading…</div>;

  const ImgField = ({ k, label }) => (
    <Field label={label}>
      <div className="flex items-center gap-3">
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {f.images[k] ? <img src={mediaUrl(f.images[k])} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-300"><ImageIcon className="h-6 w-6" /></div>}
        </div>
        <div className="flex-1">
          <input className={input} value={f.images[k]} onChange={(e) => setImg(k, e.target.value)} placeholder="Image URL or pick from library" data-testid={`page-img-${k}`} />
          <button type="button" onClick={() => setPicker(k)} className="mt-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium hover:border-brand-magenta hover:text-brand-magenta">Choose from library</button>
        </div>
      </div>
    </Field>
  );

  return (
    <div data-testid="page-editor" className="max-w-3xl">
      <button onClick={() => nav("/admin/pages")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-ink"><ArrowLeft className="h-4 w-4" /> Back to Pages</button>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold">{isNew ? "New Page" : f.title || f.slug}</h1>
        <div className="flex items-center gap-3">
          {status === "saved" && <span className="text-sm text-green-600">Saved ✓</span>}
          {status === "error" && <span className="text-sm text-red-500">Error saving</span>}
          <button onClick={save} data-testid="save-page-btn" disabled={status === "saving"} className="inline-flex items-center gap-2 rounded-full bg-brand-magenta px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
            <Save className="h-4 w-4" /> {status === "saving" ? "Saving…" : "Save Page"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Slug (URL)" hint="e.g. sound-healing → /programs/sound-healing"><input className={input} value={f.slug} disabled={!isNew} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"))} data-testid="page-slug" /></Field>
          <Field label="Category"><input className={input} value={f.category} onChange={(e) => set("category", e.target.value)} /></Field>
          <Field label="Full Title" hint="Shown in breadcrumb & CTA"><input className={input} value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="page-title" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Heading — first part" hint="e.g. Sound"><input className={input} value={f.titleLead} onChange={(e) => set("titleLead", e.target.value)} /></Field>
            <Field label="Heading — accent" hint="gradient color, e.g. Healing"><input className={input} value={f.accent} onChange={(e) => set("accent", e.target.value)} /></Field>
          </div>
        </div>
        <Field label="Subtitle"><textarea rows={2} className={input} value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="page-subtitle" /></Field>

        <div className="grid grid-cols-2 gap-4">
          <ImgField k="hero" label="Hero Image (top)" />
          <ImgField k="space" label="Overview Image (below)" />
          <ImgField k="calm" label="Quote Band Background (faded photo behind the tagline)" />
        </div>

        <Field label="Hero Slider Images (optional)" hint="One per line as: image-url | optional caption. Add 2 or more to turn the hero photo into an auto-playing slider like the homepage. Copy URLs from the Media Library.">
          <textarea rows={3} className={input} value={f.heroSlides} onChange={(e) => set("heroSlides", e.target.value)} placeholder="/api/media/file/xxxx | Optional caption" data-testid="page-hero-slides" />
        </Field>

        <Field label="Overview" hint="One paragraph per line"><textarea rows={4} className={input} value={f.overview} onChange={(e) => set("overview", e.target.value)} /></Field>
        <Field label="Practical Techniques" hint="One per line"><textarea rows={6} className={input} value={f.techniques} onChange={(e) => set("techniques", e.target.value)} /></Field>
        <Field label="Tagline (quote band)"><input className={input} value={f.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>
        <Field label="How It Helps People"><textarea rows={3} className={input} value={f.helps} onChange={(e) => set("helps", e.target.value)} /></Field>
        <Field label="Organizational Impact" hint="One per line"><textarea rows={5} className={input} value={f.orgImpact} onChange={(e) => set("orgImpact", e.target.value)} /></Field>
        <Field label="Organizational Note (optional)"><textarea rows={2} className={input} value={f.orgNote} onChange={(e) => set("orgNote", e.target.value)} /></Field>
        <Field label="Who It's For" hint="One per line"><textarea rows={4} className={input} value={f.whoFor} onChange={(e) => set("whoFor", e.target.value)} /></Field>
        <Field label="Quick Facts" hint="One per line as: Label | Value"><textarea rows={4} className={input} value={f.facts} onChange={(e) => set("facts", e.target.value)} /></Field>
        <Field label="Session Formats" hint="One per line as: Name | Time | Description"><textarea rows={4} className={input} value={f.formats} onChange={(e) => set("formats", e.target.value)} /></Field>
      </div>

      {picker && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-6" onClick={() => setPicker(null)}>
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Choose an image</h3>
              <button onClick={() => setPicker(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <MediaLibrary onPick={(url) => { setImg(picker, url); setPicker(null); }} />
          </div>
        </div>
      )}
    </div>
  );
}
